/**
 * Sections & Tables Slice
 * Redux slice for outlet sections and tables state management
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SectionsAndTablesState } from '../../types/section';
import {
  createSectionThunk,
  createTableThunk,
  fetchSections,
  fetchTables,
  updateSectionThunk,
  updateTableThunk,
} from './sectionThunks';

const initialState: SectionsAndTablesState = {
  sections: [],
  tables: [],
  localOpenDineTableIds: [],
  isLoading: false,
  error: null,
};

const sectionSlice = createSlice({
  name: 'sections',
  initialState,
  reducers: {
    clearSectionError: (state) => {
      state.error = null;
    },
    clearSections: (state) => {
      state.sections = [];
      state.tables = [];
      state.localOpenDineTableIds = [];
      state.error = null;
    },
    setLocalOpenDineTableIds: (state, action: PayloadAction<number[]>) => {
      state.localOpenDineTableIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch sections
    builder
      .addCase(fetchSections.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sections = action.payload;
      })
      .addCase(fetchSections.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create section
    builder
      .addCase(createSectionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSectionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sections.push(action.payload);
      })
      .addCase(createSectionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update section
    builder
      .addCase(updateSectionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSectionThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.sections.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (index !== -1) {
          state.sections[index] = {
            ...action.payload,
            table_count: state.sections[index].table_count,
          };
        }
      })
      .addCase(updateSectionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch tables
    builder
      .addCase(fetchTables.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTables.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tables = action.payload;
      })
      .addCase(fetchTables.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create table
    builder
      .addCase(createTableThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTableThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tables.push(action.payload);
        // Increment table count on the section
        const sectionIndex = state.sections.findIndex(
          (s) => s.id === action.payload.section_id,
        );
        if (sectionIndex !== -1) {
          state.sections[sectionIndex].table_count =
            (state.sections[sectionIndex].table_count || 0) + 1;
        }
      })
      .addCase(createTableThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update table
    builder
      .addCase(updateTableThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTableThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.tables.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tables[index] = action.payload;
        }
      })
      .addCase(updateTableThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSectionError, clearSections, setLocalOpenDineTableIds } =
  sectionSlice.actions;

export default sectionSlice.reducer;
